import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SportsTennis, SportsCricket, CheckCircleOutline, AccessAlarm } from '@mui/icons-material';
import type { League } from '../../types/database';
import colors from '../../theme/colors';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(target: Date): TimeLeft | null {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

interface Props {
  league: League;
  hasSubmittedSquad?: boolean;
}

const PreLeagueCountdown: React.FC<Props> = ({ league, hasSubmittedSquad }) => {
  const isTennis = league.sport === 'tennis';
  const accent = isTennis ? '#C8F000' : colors.blue.electric;

  const startDate = new Date(league.startDate);
  const deadlineDate = new Date(league.squadDeadline);
  const deadlinePassed = Date.now() >= deadlineDate.getTime();

  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() => getTimeLeft(startDate));
  const [deadlineLeft, setDeadlineLeft] = useState<TimeLeft | null>(() =>
    deadlinePassed ? null : getTimeLeft(deadlineDate)
  );

  useEffect(() => {
    const tick = () => {
      setTimeLeft(getTimeLeft(startDate));
      setDeadlineLeft(deadlinePassed ? null : getTimeLeft(deadlineDate));
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league.startDate, league.squadDeadline]);

  const unitBoxSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 0.5,
    px: { xs: 1.5, sm: 3 },
    py: { xs: 1.5, sm: 2 },
    borderRadius: 2,
    bgcolor: alpha(accent, 0.08),
    border: `1px solid ${alpha(accent, 0.2)}`,
    minWidth: { xs: 60, sm: 80 },
  };

  const pad = (n: number) => String(n).padStart(2, '0');

  const taglines: Record<string, string[]> = {
    tennis: [
      'Serve up your squad.',
      'The clay is calling.',
      'Glory awaits on the court.',
      'Who will lift the trophy?',
    ],
    cricket: [
      'The pitch is being prepared.',
      'Who will take the honours?',
      'Pick your XI and wait for the toss.',
    ],
  };

  const sport = isTennis ? 'tennis' : 'cricket';
  const tagline = taglines[sport][Math.floor(startDate.getTime() / 1e10) % taglines[sport].length];

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 4,
        overflow: 'hidden',
        p: { xs: 3, sm: 4 },
        background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.97)} 0%, ${alpha('#060D17', 0.99)} 100%)`,
        border: `1px solid ${alpha(accent, 0.25)}`,
        boxShadow: `0 0 60px ${alpha(accent, 0.06)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        },
      }}
    >
      {/* Sport icon + tournament label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        {isTennis
          ? <SportsTennis sx={{ color: accent, fontSize: 28 }} />
          : <SportsCricket sx={{ color: accent, fontSize: 28 }} />
        }
        <Box>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              letterSpacing: '-0.02em',
              background: `linear-gradient(90deg, #fff 60%, ${alpha(accent, 0.8)})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1.2,
            }}
          >
            {league.tournamentName || league.name}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.45), mt: 0.3 }}>
            {tagline}
          </Typography>
        </Box>
      </Box>

      {/* "Starts in" label */}
      <Typography
        variant="overline"
        sx={{ color: alpha(accent, 0.75), letterSpacing: '0.12em', fontWeight: 700, display: 'block', mb: 1.5 }}
      >
        Tournament starts in
      </Typography>

      {/* Countdown boxes */}
      {timeLeft ? (
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', mb: 3 }}>
          {[
            { value: timeLeft.days,    label: 'DAYS'  },
            { value: timeLeft.hours,   label: 'HRS'   },
            { value: timeLeft.minutes, label: 'MINS'  },
            { value: timeLeft.seconds, label: 'SECS'  },
          ].map(({ value, label }) => (
            <Box key={label} sx={unitBoxSx}>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color: accent,
                  lineHeight: 1,
                  fontSize: { xs: '1.8rem', sm: '2.4rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {pad(value)}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.35), letterSpacing: '0.1em', fontWeight: 700, fontSize: '0.65rem' }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="h6" sx={{ color: accent, mb: 3, fontWeight: 700 }}>
          Starting now!
        </Typography>
      )}

      {/* Squad status + deadline */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        {hasSubmittedSquad === true && (
          <Chip
            icon={<CheckCircleOutline sx={{ fontSize: 16, color: `${accent} !important` }} />}
            label="Your squad is locked in"
            size="small"
            sx={{
              bgcolor: alpha(accent, 0.12),
              color: accent,
              border: `1px solid ${alpha(accent, 0.3)}`,
              fontWeight: 700,
              fontSize: '0.75rem',
            }}
          />
        )}
        {hasSubmittedSquad === false && !deadlinePassed && deadlineLeft && (
          <Chip
            icon={<AccessAlarm sx={{ fontSize: 16, color: `${colors.orange.primary} !important` }} />}
            label={`Squad deadline in ${deadlineLeft.days > 0 ? `${deadlineLeft.days}d ${deadlineLeft.hours}h` : deadlineLeft.hours > 0 ? `${deadlineLeft.hours}h ${deadlineLeft.minutes}m` : `${deadlineLeft.minutes}m`}`}
            size="small"
            sx={{
              bgcolor: alpha(colors.orange.primary, 0.12),
              color: colors.orange.primary,
              border: `1px solid ${alpha(colors.orange.primary, 0.3)}`,
              fontWeight: 700,
              fontSize: '0.75rem',
            }}
          />
        )}
        {hasSubmittedSquad === false && !deadlinePassed && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4) }}>
            Submit your squad before{' '}
            <Box component="span" sx={{ color: alpha('#fff', 0.7), fontWeight: 600 }}>
              {deadlineDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Box>
          </Typography>
        )}
        {hasSubmittedSquad === false && deadlinePassed && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.4) }}>
            Squad deadline has passed.
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.3), ml: 'auto' }}>
          Kicks off {startDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
};

export default PreLeagueCountdown;
